import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { MenuOutlined } from "@ant-design/icons";

import { BSBookInfo, BSBookInfoByMonth, BOOK_TAGS, getTagConfig } from "sketon/service/data/BSBookInfo";
import { useLoadBooklist } from "sketon/service/storage/booklistLoader";

import { isEmpty } from "sketon/utility/StringUtils";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { SCREEN_SM } from "sketon/ui/theme";
import { getNavPath } from "sketon/app/RouterConfig";
import { toCoverProxyPath } from "sketon/const";
import { BSDetailPage } from "../details";
import BSBannerView from "sketon/ui/component/BannerView";
import BookListSkeleton from "sketon/ui/component/Skeleton/BookListSkeleton";
import BookCover from "sketon/ui/component/BookCover";
import Footer from "sketon/ui/component/Footer";

const SytledWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: ${SCREEN_SM}) {
    padding: 0 0rem;
  }

  position: relative;
`;

const StyledContainerDiv = styled.div`
  width: 1200px;
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;
  min-height: 100vh;

  display: flex;
  flex-flow: column;
  /* flex-start, not center: while the list is still loading the container is
     taller than its content, and centering made the banner start mid-screen
     and jump to the top once rows arrived. */
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 50px;

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    margin-bottom: 0rem;
  }
`;

const StyledSectionGroupDiv = styled.div`
  width: 100%;

  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    padding: 0 0rem;
  }
`;

const StyledSectionTitle = styled.div`
  width: 100%;
  margin-left: 0;

  border-radius: 0;

  font-size: 24px;
  line-height: 50px;
  color: rgba(17, 17, 17, 0.8);

  font-weight: bold;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.4rem;
    line-height: 0.6rem;
  }

  text-align: left;
`;

const StyledSectionHeader = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${SCREEN_SM}) {
    padding: 0 0.2rem 0.2rem;
  }
`;

const StyledNavText = styled.span`
  display: flex;
  align-items: center;
  font-size: 28px;
  line-height: 50px;
  font-weight: bold;
  color: rgba(17, 17, 17, 0.8);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.4rem;
    line-height: 0.6rem;
  }
`;

const StyledSectionTitleWrap = styled.div`
  width: 100%;
  @media (max-width: ${SCREEN_SM}) {
    padding: 0 0.2rem 0.2rem;
  }
`;

const StyledTagFilterBar = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 0 20px;

  @media (max-width: ${SCREEN_SM}) {
    padding: 0 0.2rem 10px;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledTagChip = styled.span<{ $active: boolean; $color?: string; $bgColor?: string; $isAll?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  white-space: nowrap;

  ${(props) => {
    if (props.$isAll) {
      return props.$active
        ? `background: #3A2E22; color: #FBF1DC; border: 1px solid #3A2E22;`
        : `background: #F3E5C8; color: #6E5D49; border: 1px solid transparent;`;
    }
    if (props.$active) {
      return `background: ${props.$color ?? "#B0512B"}; color: #FBF1DC; border: 1px solid ${props.$color ?? "#B0512B"};`;
    }
    return `background: ${props.$bgColor ?? "#F6E7D2"}; color: ${props.$color ?? "#B0512B"}; border: 1px solid transparent;`;
  }}

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.24rem;
    padding: 2px 0.16rem;
  }
`;

const StyledListItemDiv = styled.div`
  width: 100%;
  padding: 0px 0px 20px;

  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;

  flex-wrap: wrap;

  background: #FFFFFF;
  border: 1px solid #EBD9B0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(58, 46, 34, 0.06);

  margin-bottom: 30px;

  @media (max-width: ${SCREEN_SM}) {
    justify-content: space-between;
    width: 100%;
    padding: 0.1rem 0.4rem;
    border-radius: 0px;
    margin-bottom: 10px;
  }
`;

const StyledBookItem = styled.div`
  width: fit-content;
  padding: 20px 0px 0px 20px;
  min-height: 155px;
  margin-top: 0;
  margin-left: 0;

  display: flex;
  flex-flow: row;
  justify-content: flex-start;
  align-items: flex-start;

  cursor: pointer;
  transition: 0.2s;

  :hover {
    transform: translateX(10px);
  }
  :active {
    transform: translateX(10px);
  }

  @media (min-width: ${SCREEN_SM}) {
  }

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    justify-content: flex-start;
    padding: 0.24rem 0;
    min-height: 0;

    &:not(:last-child) {
      border-bottom: 1px solid #f0e4c8;
    }
  }
`;

const StyledBookRight = styled.div`
  margin-top: 0;
  margin-left: 20px;
  padding: 0 0 0 0;
  border-radius: 0;

  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: left;
  position: relative;

  @media (min-width: ${SCREEN_SM}) {
    width: 350px;
  }

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    margin-left: 0.24rem;
    align-self: stretch;
  }
`;

const StyledBookNameDiv = styled.div`
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;

  border-radius: 0;

  text-align: left;

  font-size: 24px;
  line-height: 40px;
  color: #3A2E22;
  text-align: left;
  font-weight: bold;
  font-family: "Noto Serif SC", "Songti SC", "STSong", serif;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  max-width: 100%;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.42rem;
    line-height: 0.54rem;
  }
`;

const StyledBookAuthorDiv = styled.div`
  margin-top: 10px;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  text-align: left;

  font-size: 20px;
  line-height: 30px;
  color: #8A7B64;
  text-align: left;
  font-weight: normal;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.18rem;
    font-size: 0.32rem;
    line-height: 0.44rem;
  }
`;

/* Author + date read as one block, pinned to the bottom of the row on
   mobile so the date baseline lines up with the bottom of the cover. */
const StyledBookMetaDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: auto;
  }
`;

const StyledRecommedTimeDiv = styled.div`
  margin-top: 0px;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  text-align: left;

  font-size: 20px;
  line-height: 30px;
  color: #A89A80;
  text-align: left;
  font-weight: normal;
  white-space: pre-space;

  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.06rem;
    font-size: 0.3rem;
    line-height: 0.42rem;
  }
`;

const StyledBookTag = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-top: 8px;
  padding: 2px 10px;
  border-radius: 4px;
  background: ${(props) => props.$color + "1A"};
  color: ${(props) => props.$color};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  white-space: nowrap;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.08rem;
    font-size: 0.22rem;
    padding: 1px 0.12rem;
    letter-spacing: 0.5px;
  }
`;

const StyledBookNameTagWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BookNameWithTag: React.FC<{
  name: string;
  tagColor: string;
  tagLabel: string;
}> = ({ name, tagColor, tagLabel }) => {
  return (
    <StyledBookNameTagWrap>
      <StyledBookNameDiv>{name}</StyledBookNameDiv>
      <StyledBookTag $color={tagColor}>{tagLabel}</StyledBookTag>
    </StyledBookNameTagWrap>
  );
};

export interface BSMainPageProps {}
export const BSMainPage: React.FC<BSMainPageProps> = () => {
  const list = useLoadBooklist();

  const [books, setBooks] = useState<BSBookInfoByMonth[]>();
  const [activeTag, setActiveTag] = useState<string>("");

  const groupListByMonth = useCallback(
    (filtered: BSBookInfo[]) => {
      var groups = new Map();
      filtered?.map((info: BSBookInfo) => {
        if (!isEmpty(info.date)) {
          var mmt = moment(info.date, "YYYY/MM/DD");
          info.date_timestamp = mmt.valueOf();
          var key = mmt.set("date", 1).valueOf();

          var infoArray = [];
          if (groups.has(key)) {
            infoArray = groups.get(key);
          }

          infoArray.push(info);

          groups.set(key, infoArray);
        }

        return 0;
      });

      var temp: BSBookInfoByMonth[] = [];
      groups.forEach((value, key, map) => {
        var item: BSBookInfoByMonth = {
          month: moment(key).format("YYYY年MM月"),
          books: value,
        };
        temp.push(item);
      });

      setBooks(temp.reverse());
    },
    []
  );

  const navigate = useNavigate();
  const gotoDetail = useCallback(
    (isbn: string) => {
      navigate(getNavPath(BSDetailPage.name) + "/" + isbn, { state: { from: "/" } });
    },
    [navigate]
  );

  const gotoYear = useCallback(
    (year: number) => {
      navigate("/books/" + year);
    },
    [navigate]
  );

  // Extract available years from book data
  const availableYears = useMemo((): number[] => {
    if (!list || list.length === 0) return [];
    const years = new Set<number>();
    list.forEach((info) => {
      if (!isEmpty(info.date)) {
        const mmt = moment(info.date, "YYYY/MM/DD");
        if (mmt.isValid()) {
          years.add(mmt.year());
        }
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [list]);

  // Extract available tags from book data
  const availableTags = useMemo((): string[] => {
    if (!list || list.length === 0) return [];
    const tagSet = new Set<string>();
    list.forEach((info) => {
      const tag = info.tag || "other";
      tagSet.add(tag);
    });
    return BOOK_TAGS.filter((t) => tagSet.has(t.key)).map((t) => t.key);
  }, [list]);

  // Filter books by active tag
  const filteredList = useMemo((): BSBookInfo[] | undefined => {
    if (!list) return undefined;
    if (!activeTag) return list;
    return list.filter((info) => (info.tag || "other") === activeTag);
  }, [list, activeTag]);

  const currentYear = new Date().getFullYear();

  // Build dropdown menu items
  const yearMenuItems: MenuProps["items"] = availableYears.map((year) => ({
    key: String(year),
    label: `${year}年`,
    style: { whiteSpace: "nowrap" },
  }));

  const handleYearMenuClick: MenuProps["onClick"] = ({ key }) => {
    gotoYear(Number(key));
  };

  useEffect(() => {
    if (filteredList !== undefined) {
      groupListByMonth(filteredList);
    }
  }, [filteredList, groupListByMonth]);

  return (
    <SytledWrapper>
      <StyledContainerDiv>
        <BSBannerView></BSBannerView>

        {/* Tag filter bar */}
        {list && list.length > 0 && (
        <StyledTagFilterBar>
          <StyledTagChip
            $isAll
            $active={activeTag === ""}
            onClick={() => setActiveTag("")}
          >
            全部
          </StyledTagChip>
          {availableTags.map((tagKey) => {
            const cfg = getTagConfig(tagKey);
            return (
              <StyledTagChip
                key={tagKey}
                $active={activeTag === tagKey}
                $color={cfg.color}
                $bgColor={cfg.bgColor}
                onClick={() => setActiveTag(activeTag === tagKey ? "" : tagKey)}
              >
                {cfg.label}
              </StyledTagChip>
            );
          })}
        </StyledTagFilterBar>
        )}

        {/* Covers both the fetch and the grouping pass that follows it, so the
            placeholder never blinks out between the two. The tag bar part is
            dropped as soon as the real chips can render. */}
        {books === undefined && (
          <BookListSkeleton rows={5} showTagBar={list === undefined} />
        )}

        {books?.map((booksInMonth, index) => (
          <StyledSectionGroupDiv key={index}>
            {index === 0 ? (
              <StyledSectionHeader>
                <StyledSectionTitle>{booksInMonth.month}</StyledSectionTitle>
                <div style={{ display: "flex", flexFlow: "row", alignItems: "center" }}>
                  <StyledNavText>
                    <span onClick={() => gotoYear(currentYear)} style={{ cursor: "pointer" }}>
                      {currentYear === new Date().getFullYear() ? "今年" : `${currentYear}年`}
                    </span>
                    <Dropdown menu={{ items: yearMenuItems, onClick: handleYearMenuClick }} trigger={["hover", "click"]}>
                      <MenuOutlined style={{ fontSize: "0.55em", marginLeft: "12px", cursor: "pointer", color: "#555" }} />
                    </Dropdown>
                  </StyledNavText>
                </div>
              </StyledSectionHeader>
            ) : (
              <StyledSectionTitleWrap>
                <StyledSectionTitle>{booksInMonth.month}</StyledSectionTitle>
              </StyledSectionTitleWrap>
            )}
            <StyledListItemDiv>
              {booksInMonth.books?.map((info: BSBookInfo) => {
                const tagCfg = getTagConfig(info.tag);
                return (
                  <StyledBookItem key={info.isbn} onClick={() => gotoDetail(info.isbn)}>
                    <BookCover
                      src={toCoverProxyPath(info.cover)}
                      alt={info.title}
                    />
                    <StyledBookRight>
                      <BookNameWithTag
                        name={info.title}
                        tagColor={tagCfg.color}
                        tagLabel={tagCfg.label}
                      />
                      <StyledBookMetaDiv>
                        <StyledBookAuthorDiv>{info.author} </StyledBookAuthorDiv>
                        <StyledRecommedTimeDiv>
                          推荐日期：{info.date}
                        </StyledRecommedTimeDiv>
                      </StyledBookMetaDiv>
                    </StyledBookRight>
                  </StyledBookItem>
                );
              })}
            </StyledListItemDiv>
          </StyledSectionGroupDiv>
        ))}
      </StyledContainerDiv>

      <Footer></Footer>
    </SytledWrapper>
  );
};
